import React, { useEffect, useState } from "react";
import "./HouseDetail.css";
import { API_BASE_URL, API_KEY } from "../../config.js";
import { useParams } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import defaultImage from "../../assets/default.png";
import SimpleImageViewer from "react-simple-image-viewer";

import wifi from "../../assets/Icons/facilities/wifi.png";
import tv from "../../assets/Icons/facilities/tv.png";
import shower from "../../assets/Icons/facilities/shower.png";
import workspace from "../../assets/Icons/facilities/workspace.png";
import airCondition from "../../assets/Icons/facilities/air_condition.png";
import balcony from "../../assets/Icons/facilities/balcony.png";
import heating from "../../assets/Icons/facilities/heating.png";
import kitchen from "../../assets/Icons/facilities/kitchen.png";
import laundry from "../../assets/Icons/facilities/laundry.png";
import privateBathroom from "../../assets/Icons/facilities/bathroom.png";

import location from "../../assets/Icons/location_icon.png";
import TelegramUser from "../../TelegramUser.js";
import TelegramBackButton from "../../components/TelegramBackButton.jsx";
import LoadingAnim from "../../components/LoadingAnim.jsx";
import LazyLoad from "react-lazyload";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useTranslation } from "react-i18next";
import PendingModal from "./Components/PendingModal.jsx";
import BookingForm from "./Components/BookingForm.jsx";

// HouseDetail component
const HouseDetail = () => {
  const { t } = useTranslation();
  const isHomePage = false;

  const [showPendingModal, setShowPendingModal] = useState(false);
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { toggleFooter } = useOutletContext();

  useEffect(() => {
    toggleFooter(!isModalOpen);

    const initData = window.Telegram.WebApp.initData;
    const fetchPropertyDetail = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/user/api/v1/house_detail/?id=${id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": API_KEY,
              "X-Telegram-InitData": initData,
            },
          }
        );

        const data = await response.json();

        if (data.houseImage && data.houseImage.length > 0) {
          const imageUrls = data.houseImage.map((imageObj) => imageObj.image);
          data.houseImageSrc = imageUrls;
        }

        setProperty(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyDetail();
    setUserInfo(TelegramUser);
  }, [id, isModalOpen, toggleFooter]);

  const handleBookClick = () => {
    setShowModal(true);
    setShowPendingModal(false);
    setIsModalOpen(true);
    toggleFooter(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsModalOpen(false);
    toggleFooter(true);
  };

  const openImageViewer = (index) => {
    setCurrentImageIndex(index);
    setIsViewerOpen(true);
  };

  const closeImageViewer = () => {
    setIsViewerOpen(false);
  };

  if (loading) {
    return <LoadingAnim />;
  }

  if (error) {
    return (
      <p>
        {t("Error")}: {error}
      </p>
    );
  }

  if (!property) {
    return <p>{t("No property details available")}.</p>;
  }

  const items = property?.sub_description.split(",");
  
  const emojiMap = {
    "Wi-Fi": wifi,
    "Hot Shower": shower,
    "Kitchen Access": kitchen,
    "Private Bathroom": privateBathroom,
    "Priv. Bathroom": privateBathroom,
    "TV": tv,
    "Laundry Facilities": laundry,
    "Workspace/Desk": workspace,
    "Balcony or Terrace": balcony,
    "Air Conditioning": airCondition,
    "A/C": airCondition,
    "Heating": heating,
    "Pvt Bath": privateBathroom,
    "Workspace": workspace, 
    "Balcony": balcony, 
    "Laundry": laundry, 
  };

  const facilitiesToRender = items.slice(1);

  return (
    <>
      {showPendingModal && (
        <PendingModal onClose={() => setShowPendingModal(false)} />
      )}
      <TelegramBackButton isHomePage={isHomePage} />

      <div className="max-w-lg mx-auto bg-white text-white p-4 rounded-lg">
        <div className="flex flex-col items-center space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
          {property.houseImageSrc && property.houseImageSrc.length > 0 ? (
            <Swiper
              spaceBetween={4}
              slidesPerView={1}
              pagination={{ clickable: true }}
              modules={[Pagination]}
              className="property-swiper max-w-full sm:max-w-xs md:max-w-sm lg:max-w-md"
            >
              {property.houseImageSrc.map((src, index) => (
                <SwiperSlide key={index}>
                  <LazyLoad height={350} offset={100}>
                    <img
                      src={src}
                      alt="house-icon"
                      className="w-full h-auto cursor-pointer"
                      style={{
                        maxHeight: "260px",
                        maxWidth: "100%",
                        borderRadius: "10px",
                        objectFit: "contain",
                      }}
                      onClick={() => openImageViewer(index)}
                    />
                  </LazyLoad>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <img
              src={defaultImage}
              alt={property.title}
              className="w-full"
              style={{ borderRadius: "5px", height: "350px" }}
            />
          )}
        </div>
        <br />
        <div>
          <p className="text-lg font-bold text-black sm:text-xl md:text-2xl">
            {property?.title}
          </p>
          <p className="text-sm text-gray-400 sm:text-base md:text-lg">
            {property?.description}
          </p>
          <p className="text-sm text-black sm:text-base md:text-lg">
            <b>{t("posted by")} </b>{" "}
            {property?.postedBy?.userAccount?.first_name}
          </p>
        </div>
        <br />
        <div className="text-sm text-black">
          <p className="flex items-center">
            <img
              src={location}
              alt="location icon"
              width={25}
              height={25}
              className="mr-2"
            />
            {property?.city}, {property?.specificAddress}
          </p>
        </div>
        <br />
        <div
          //  className="flex flex-col mt-2"
          style={{
            backgroundColor: "#f2f4f7",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <p className="text-black font-bold sm:text-base md:text-lg lg:text-xl">
            {t("Facilities")}
          </p>

          <div style={{ overflow: "hidden" }}>
            <div className="grid grid-cols-4 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-2 mt-2">
              {facilitiesToRender.map((item, index) => {
                const trimmedItem = item.trim();
                const emojiOrIcon = emojiMap[trimmedItem];

                return (
                  <div
                    key={index}
                    className="flex flex-col items-center text-black p-2"
                  >
                    {emojiOrIcon ? (
                      <>
                        <img
                          src={emojiOrIcon}
                          alt={trimmedItem}
                          className="w-10 h-10"
                        />
                        <span className="text-[10px] mt-2 text-center">
                          {t(trimmedItem)}
                        </span>
                      </>
                    ) : (
                      <span className="text-[10px] text-center">
                        {t(trimmedItem)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <br />
        <hr></hr>
        <br />
        <div className="flex items-center justify-between p-2 sm:p-3 md:p-4">
          <div>
            <p className="text-black font-bold text-sm sm:text-base md:text-lg">
              {property?.price} {property?.unit}
            </p>
            <p className="text-sm text-gray-400">{t("per day")}</p>
          </div>

          <div>
            <button
              onClick={handleBookClick}
              className="text-white py-3 px-20 rounded-xl"
              style={{ backgroundColor: "#6938ef" }}
            >
              {t("Book")}
            </button>
          </div>
        </div>
        <BookingForm
          showModal={showModal}
          onClose={handleCloseModal}
          userInfo={userInfo}
          houseId={id}
          setShowPendingModal={setShowPendingModal}
        />
      </div>
      {isViewerOpen && (
        <SimpleImageViewer
          src={property.houseImageSrc}
          currentIndex={currentImageIndex}
          onClose={closeImageViewer}
          disableScroll={false}
          backgroundStyle={{ backgroundColor: "rgba(0, 0, 0, 0.9)" }}
        />
      )}
    </>
  );
};

export default HouseDetail;
